import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';
import styled from '@emotion/styled';
import useWindowDimensions from 'ui_system/utilities/useWindowDimensions';
import { colors, fontSizes, shadows, shape, spacing } from '../utils';
import {
  ChartWrapper,
  OverflowContainer,
  StickyYAxis,
  Watermark,
} from '../chart_styles/styles';

const tooltipNames = {
  detractors_percentage: 'Detractors',
  nps: 'Net Promoter Score (NPS)',
  passives_percentage: 'Passives',
  promoters_percentage: 'Promoters',
};

const defaultTooltipWidth = 280;

export const StickyBottomXAxisWrapper = styled.div`
  width: 100%;
  ${(props) => props.minWidth && `min-width: ${props.minWidth}px`};
  ${(props) => props.maxWidth && `max-width: ${props.maxWidth}px`};
`;

export const StickyBottomXAxis = ({
  chartMargins,
  data,
  layout,
  maxWidth,
  minWidth,
  stackOrder,
  xAxisBottom,
  xAxisTop,
  yAxis,
}) => {
  const stackedBars = stackOrder.map((barKey) => (
    <Bar
      isAnimationActive={false}
      dataKey={barKey}
      fill="transparent"
      key={barKey}
      legendType="none"
      name={barKey}
      stackId="npschart"
      stackOffset="sign"
      xAxisId={xAxisBottom.xAxisId}
      yAxisId={yAxis.yAxisId}
    />
  ));
  return (
    <StickyBottomXAxisWrapper maxWidth={maxWidth} minWidth={minWidth}>
      <ResponsiveContainer
        height={60}
        maxWidth={maxWidth}
        minWidth={minWidth}
        width="100%"
      >
        <ComposedChart
          data={data}
          layout={layout}
          margin={{ ...chartMargins, bottom: 25 }}
        >
          <XAxis {...xAxisTop} hide />
          <XAxis {...xAxisBottom} hide={false} />
          <YAxis {...yAxis} axisLine={false} label={false} tick={false} />
          {stackedBars}
        </ComposedChart>
      </ResponsiveContainer>
    </StickyBottomXAxisWrapper>
  );
};

const StickyTopXAxisWrapper = styled.div`
  width: 100%;
  ${(props) => props.minWidth && `min-width: ${props.minWidth}px`};
  ${(props) => props.maxWidth && `max-width: ${props.maxWidth}px`};
`;

const StickyTopXAxis = ({
  chartMargins,
  data,
  layout,
  maxWidth,
  minWidth,
  stackOrder,
  xAxisBottom,
  xAxisTop,
  yAxis,
}) => {
  const stackedBars = stackOrder.map((barKey) => (
    <Bar
      isAnimationActive={false}
      dataKey={barKey}
      fill="transparent"
      key={barKey}
      legendType="none"
      name={barKey}
      stackId="npschart"
      stackOffset="sign"
      xAxisId={xAxisBottom.xAxisId}
      yAxisId={yAxis.yAxisId}
    />
  ));
  return (
    <StickyTopXAxisWrapper maxWidth={maxWidth} minWidth={minWidth}>
      <ResponsiveContainer
        height={70}
        maxWidth={maxWidth}
        minWidth={minWidth}
        width="100%"
      >
        <ComposedChart
          data={data}
          layout={layout}
          margin={{ ...chartMargins, top: 35 }}
        >
          <CartesianGrid
            horizontal={layout === 'horizontal'}
            stroke={colors.graphLine}
            vertical={layout === 'vertical'}
          />
          <XAxis {...xAxisTop} hide={false} />
          <XAxis {...xAxisBottom} hide />
          <YAxis {...yAxis} label={false} tick={false} />
          {stackedBars}
        </ComposedChart>
      </ResponsiveContainer>
    </StickyTopXAxisWrapper>
  );
};

const TooltipWrapper = styled.div`
  background: ${colors.white};
  box-shadow: ${shadows.primary};
  font-family: Soleil;
  font-size: 13px;
  left: ${(props) => props.x}px;
  position: absolute;
  top: ${(props) => props.y}px;
  width: ${defaultTooltipWidth}px;
  z-index: 100;
`;

const TooltipData = styled.div`
  padding: ${spacing.medium} ${spacing.none} ${spacing.none} ${spacing.medium};
`;

const TooltipLabel = styled.p`
  color: ${colors.text.secondary};
  font-family: Soleil;
  font-size: 11px;
  font-weight: 400;
  margin: 0;
  margin-bottom: ${spacing.xxsmall};
  text-transform: uppercase;
`;

const TooltipLegend = styled.div`
  display: flex;
  flex-direction: column;
  padding-right: ${spacing.medium};
`;

const TooltipLegendSquare = styled.div`
  background: ${(props) => props.color};
  border-radius: ${shape.borderRadius};
  height: ${spacing.medium};
  margin-right: ${spacing.xsmall};
  width: ${spacing.medium};
`;

const TooltipLegendSquareLabel = styled.div`
  align-items: center;
  display: flex;
  padding-right: ${spacing.medium};
`;

const TooltipLegendValue = styled.div`
  display: flex;
  font-weight: 600;
`;

const TooltipValue = styled.div`
  color: ${colors.text.primary};
  font-weight: 600;
  margin: 0;
  margin-bottom: ${spacing.medium};
`;

// Copied from BarChartGroup
const TooltipLabelValueGroup = styled.div`
  margin-top: ${spacing.xxsmall};
  padding-right: ${spacing.medium};
`;

const TooltipValueAndBenchmark = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TooltipBenchmarkData = styled.div`
  background: ${colors.background.light};
  margin-bottom: ${spacing.medium};
  padding: ${spacing.xsmall};
`;

const TooltipBenchmarkLabel = styled.div`
  color: ${colors.text.icon};
  font-size: 9px;
  padding-bottom: ${spacing.xxsmall};
`;

const TooltipBenchmarkRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TooltipBenchmarkValue = styled.div`
  color: ${colors.text.primary};
  font-size: 13px;
  padding-right: ${spacing.small};
`;

const getDiffColor = (diff) => {
  if (diff === 0) {
    return colors.data.secondary;
  }
  if (diff < 0) {
    return colors.data.negative;
  }
  return colors.data.positive;
};

const TooltipBenchmarkDiff = styled.div`
  color: ${(props) => getDiffColor(props.diff)};
`;

const TooltipLegendItem = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${(props) => (props.noMargin ? spacing.none : spacing.xsmall)};
`;

const TooltipResponseCount = styled.p`
  color: ${colors.text.secondary};
  font-size: ${fontSizes.xsmall};
  padding: ${spacing.medium};
`;

const formatDiff = (diff) => {
  const absDiff = Math.abs(diff);
  const formattedValue = `${Math.round(absDiff)}`;
  return `${diff < 0 ? '-' : '+'}${formattedValue}`;
};

const noBenchmarkData = (value) => value !== 0 && !value;

export const CustomTooltip = (props) => {
  const { active, benchmark, coordinates, dimensions, label, name, payload } =
    props;
  const target = document.body;
  if (!active || !coordinates) return null;

  // Order of payloads matches render order
  const legendBreakdown = payload.slice(0, 3);
  const benchmarkLoad = benchmark && payload[3];
  const totalNPS = benchmark ? payload[4] : payload[3];
  const formattedNPS =
    typeof totalNPS?.value === 'number' ? Math.round(totalNPS.value) : 'n/a';
  const totalResponses = totalNPS?.payload?.unit_count;
  const diff =
    benchmarkLoad &&
    Math.round(totalNPS.value || 0) - Math.round(benchmarkLoad.value || 0);
  const formattedBenchmarkValue = noBenchmarkData(benchmarkLoad?.value)
    ? 'n/a'
    : Math.round(benchmarkLoad.value);
  // Do not show diff if we do not have partner data or benchmark data
  const formattedDiff =
    noBenchmarkData(benchmarkLoad?.value) || totalNPS?.value === null
      ? ''
      : formatDiff(diff);

  const { width } = dimensions;
  let x = coordinates[0] + 10;
  const y = coordinates[1];
  if (x + defaultTooltipWidth >= width) {
    x -= defaultTooltipWidth + 20;
  }

  const TooltipPortal = (
    <TooltipWrapper x={x} y={y}>
      <TooltipData>
        {name && (
          <>
            <TooltipLabel>{name}</TooltipLabel>
            <TooltipValue>{label}</TooltipValue>
          </>
        )}
        <TooltipValueAndBenchmark>
          <TooltipLabelValueGroup>
            <TooltipLabel>{tooltipNames.nps}</TooltipLabel>
            <TooltipValue>{formattedNPS}</TooltipValue>
          </TooltipLabelValueGroup>
          {benchmark && (
            <TooltipBenchmarkData>
              <TooltipBenchmarkLabel>{benchmark}</TooltipBenchmarkLabel>
              <TooltipBenchmarkRow>
                <TooltipBenchmarkValue>
                  {formattedBenchmarkValue}
                </TooltipBenchmarkValue>
                <TooltipBenchmarkDiff diff={diff}>
                  {formattedDiff}
                </TooltipBenchmarkDiff>
              </TooltipBenchmarkRow>
            </TooltipBenchmarkData>
          )}
        </TooltipValueAndBenchmark>
        {typeof totalNPS?.value === 'number' && (
          <TooltipLegend>
            {legendBreakdown.map((item) => (
              <TooltipLegendItem key={item.name}>
                <TooltipLegendSquareLabel>
                  <TooltipLegendSquare color={item.fill} />
                  {tooltipNames[item.name]}
                </TooltipLegendSquareLabel>
                <TooltipLegendValue>
                  {Math.round(item.value)}%
                </TooltipLegendValue>
              </TooltipLegendItem>
            ))}
          </TooltipLegend>
        )}
      </TooltipData>
      {!!totalResponses && (
        <TooltipResponseCount>
          Based on {totalResponses} responses
        </TooltipResponseCount>
      )}
    </TooltipWrapper>
  );

  return createPortal(TooltipPortal, target);
};

// Custom "dot" for the Scatter chart
const BenchmarkLine = ({
  cx,
  cy,
  stroke,
  strokeDasharray,
  strokeWidth,
  yAxis: { bandSize },
}) => {
  const yOffset = Math.round(bandSize / 2);
  return (
    <path
      d={`M ${cx} ${cy - yOffset} L ${cx} ${cy + yOffset}`}
      style={{
        stroke,
        strokeWidth,
        strokeDasharray,
      }}
    />
  );
};

export const NPSChart = ({
  animationTime = 400,
  barCategoryGap = 6,
  barGap = 6,
  barName = '',
  barSize,
  benchmarkProps,
  data = [],
  dataKey,
  disableAnimations = false,
  downloadRef,
  height = 400,
  includeWatermark = false,
  layout = 'vertical',
  legendType = 'none',
  lineChartProps = {},
  maxBarSize = 60,
  maxHeight,
  maxWidth,
  minWidth,
  stackColors = [],
  stackOrder = [],
  stickyAxes = false,
  tooltipFormatter,
  unit,
  xAxisId,
  xAxisProps = {},
  yAxisId,
  yAxisProps = {},
}) => {
  const dimensions = useWindowDimensions();
  const containerRef = useRef(null);
  const [mouseCoordinates, setMouseCoordinates] = useState(null);
  const chartMargins = { bottom: 25, left: 20, top: 25, right: 20 };
  let maxChartHeight = maxHeight;
  let maxChartWidth = maxWidth;
  let minChartWidth = minWidth + (yAxisProps?.width || 60); // 60 is default yAxis width
  const { bottom: xAxisBottom, top: xAxisTop } = xAxisProps;
  const { left: yAxisLeft, right: yAxisRight } = yAxisProps;
  let yAxisLeftLabel = yAxisLeft?.label;

  if (stickyAxes) {
    // Using external x-axis on top and bottom instead of the chart's to create "sticky" effect
    // Using external y-axis labels instead of rechart's built-in ones to create "sticky" effect
    yAxisLeftLabel = false;
    maxChartHeight -= 70 * 2;
    maxChartWidth -= 50;
    minChartWidth -= 50;
    chartMargins.bottom = 0;
    chartMargins.left = 0;
    chartMargins.top = 0;
  }

  const onMouseMove = (_, e) => {
    setMouseCoordinates([e.clientX, e.clientY]);
  };

  const stackedBars = stackOrder.map((barKey, index) => (
    <Bar
      animationBegin={index * animationTime}
      animationDuration={animationTime}
      animationEasing="linear"
      barSize={barSize}
      bottom={10}
      dataKey={barKey}
      fill={stackColors[index]}
      key={barKey}
      id={barKey}
      isAnimationActive={!disableAnimations}
      layout={layout}
      legendType={legendType}
      maxBarSize={maxBarSize}
      name={barKey}
      stackId="npschart"
      stackOffset="sign"
      unit={unit}
      xAxisId={xAxisId}
      yAxisId={yAxisId}
    />
  ));

  return (
    <>
      {stickyAxes && (
        <StickyYAxis label={yAxisLeft?.label?.value} maxHeight={maxHeight} />
      )}
      <ChartWrapper maxWidth={maxChartWidth} minWidth={minChartWidth}>
        {stickyAxes && (
          <StickyTopXAxis
            barName={barName}
            chartMargins={chartMargins}
            data={data}
            dataKey={dataKey}
            layout={layout}
            maxWidth={maxChartWidth}
            minWidth={minChartWidth}
            stackOrder={stackOrder}
            xAxisBottom={xAxisBottom}
            xAxisTop={xAxisTop}
            yAxis={yAxisLeft}
          />
        )}
        <OverflowContainer
          maxHeight={maxChartHeight}
          maxWidth={maxChartWidth}
          minWidth={minChartWidth}
        >
          {includeWatermark && (
            <Watermark bottom={6} right={chartMargins.right} />
          )}
          <ResponsiveContainer
            height={height}
            minWidth={minChartWidth}
            ref={containerRef}
            width="100%"
          >
            <ComposedChart
              barCategoryGap={barCategoryGap}
              barGap={barGap}
              barSize={barSize}
              data={data}
              height={height}
              layout={layout}
              margin={chartMargins}
              onMouseMove={onMouseMove}
              ref={downloadRef}
              stackOffset="sign"
            >
              <CartesianGrid
                horizontal={layout === 'horizontal'}
                stroke={colors.graphLine}
                vertical={layout === 'vertical'}
              />
              {xAxisTop && <XAxis {...xAxisTop} hide={stickyAxes} />}
              {xAxisBottom && <XAxis {...xAxisBottom} hide={stickyAxes} />}
              {yAxisLeft && <YAxis {...yAxisLeft} label={yAxisLeftLabel} />}
              {yAxisRight && <YAxis {...yAxisRight} />}
              <ZAxis dataKey="nps" range={lineChartProps.range} type="number" />
              <Tooltip
                benchmark={benchmarkProps?.name}
                containerRef={containerRef}
                content={<CustomTooltip name={barName} />}
                coordinates={mouseCoordinates}
                cursor={false}
                dimensions={dimensions}
                filterNull={false}
                formatter={tooltipFormatter}
              />
              {stackedBars}
              {benchmarkProps && layout === 'vertical' && (
                <Scatter
                  {...benchmarkProps}
                  isAnimationActive={!disableAnimations}
                  shape={<BenchmarkLine />}
                />
              )}
              {benchmarkProps && layout === 'horizontal' && (
                <Line
                  {...benchmarkProps}
                  isAnimationActive={!disableAnimations}
                />
              )}
              <Line
                {...lineChartProps}
                isAnimationActive={!disableAnimations}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </OverflowContainer>
        {stickyAxes && (
          <StickyBottomXAxis
            barName={barName}
            chartMargins={chartMargins}
            data={data}
            dataKey={dataKey}
            layout={layout}
            maxWidth={maxChartWidth}
            minWidth={minChartWidth}
            stackOrder={stackOrder}
            xAxisBottom={xAxisBottom}
            xAxisTop={xAxisTop}
            yAxis={yAxisLeft}
          />
        )}
      </ChartWrapper>
    </>
  );
};

NPSChart.displayName = 'NPSChart';
